// M040 C2: hardcoded colors → tokens, text-* → text-tok-*
// data-fit LLC · DecisionFlow · EXECUTE Stage
// ReadinessGateDashboard.jsx · DDL: R5 · API: v1.11 · Session 92
// Auth: useAuth() direct — ARCH-2 compliant
// Key constraints: exact readiness enums, exact entity enums, no tenant_id in requests
// PATCH-S92-01: entity_id → entity_uuid (DDL column: data_readiness_status.entity_uuid)
// Patched: S205 — CSS fix: bare class names replaced with Tailwind utilities (no new CSS files)

import React, { useEffect, useMemo, useState } from 'react'
import { useAuth } from '../../auth/AuthContext.jsx'
import { SCREEN_ORDER } from './executeFlowConstants'

import Pill from '../shared/Pill'

// AFTER — C4 (S122)
const READINESS_STATUSES = [
  'imported',
  'validated',
  'exception',
  'assignment_ready',
  'quota_pending',
  'quota_not_required',
  'credit_ready',
  'preview_ready',
  'payroll_ready',
  'closed',
]

const ENTITY_TYPES = [
  'position',
  'quota',
  'credit',
  'plan_assignment',
  'tenant_period',
]

// C4 (S122): maps readiness_status → Pill tone + display label
const READINESS_PILL = {
  imported:            { tone: 'Draft',     label: 'Imported' },
  validated:           { tone: 'Ready',     label: 'Validated' },
  exception:           { tone: 'Blocked',   label: 'Exception' },
  assignment_ready:    { tone: 'Ready',     label: 'Assignment Ready' },
  quota_pending:       { tone: 'Pending',   label: 'Quota Pending' },
  quota_not_required:  { tone: 'Complete',  label: 'Not Required' },
  credit_ready:        { tone: 'Ready',     label: 'Credit Ready' },
  preview_ready:       { tone: 'Preview',   label: 'Preview Ready' },
  payroll_ready:       { tone: 'Complete',  label: 'Payroll Ready' },
  closed:              { tone: 'Complete',  label: 'Closed' },
}

function getErrorMessage(payload) {
  const error = payload?.error
  if (!error) return 'Unexpected readiness error.'
  if (error.code === 'AUTH_INSUFFICIENT_ROLE') return 'You do not have the comp_admin role required to view readiness.'
  if (error.code === 'FEATURE_UNAVAILABLE') return 'This readiness feature is unavailable for the current tenant tier.'
  return error.recovery_action || error.message || 'Unable to load readiness.'
}

export default function ReadinessGateDashboard({ fiscalPeriodId = 6174 }) {
  const { token } = useAuth()

  const [rows, setRows] = useState([])
  const [summary, setSummary] = useState(null)
  const [loading, setLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [lastLoadedAt, setLastLoadedAt] = useState(null)

  const periodId = useMemo(() => {
    const parsed = Number(fiscalPeriodId)
    return Number.isInteger(parsed) ? parsed : null
  }, [fiscalPeriodId])

  const loadReadiness = async () => {
    if (!periodId) {
      setErrorMessage('A valid integer fiscal_period_id is required.')
      return
    }

    setLoading(true)
    setErrorMessage('')

    try {
      // OI-EXEC-READINESS-QUERY-PARAMS: GET /api/v1/readiness-status query params not confirmed in v1.11
      const response = await fetch(
        `/api/v1/readiness-status?fiscal_period_id=${encodeURIComponent(periodId)}`,
        { credentials: 'include' }
      )

      const payload = await response.json().catch(() => ({}))

      if (!response.ok) {
        throw new Error(getErrorMessage(payload))
      }

      // API returns summary object with domains[] not a flat rows array
      let nextRows = []
      if (payload?.domains && Array.isArray(payload.domains)) {
        nextRows = payload.domains.map(d => ({
          entity_type: d.entity_type,
          readiness_status: d.min_status || payload.data_readiness_status,
          entity_uuid: `${d.entity_type}-summary`,
          updated_at: payload.status_updated_at || null,
          _count: d.count,
          _blocking: d.blocking_issue_count,
          _summary: payload,
        }))
      } else {
        nextRows = Array.isArray(payload?.data) ? payload.data
          : Array.isArray(payload) ? payload : []
      }
      setRows(nextRows)
      // Store summary for gate display
      setSummary(payload?.domains ? payload : null)
      setLastLoadedAt(new Date())
    } catch (error) {
      setErrorMessage(error.message || 'Unable to load readiness.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadReadiness()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periodId])

  const groupedRows = useMemo(() =>
    ENTITY_TYPES.map((entityType) => ({
      entityType,
      items: rows.filter((row) => row.entity_type === entityType),
    })), [rows])

  const hasBlockingException = summary
    ? (summary.blocking_issue_count > 0)
    : rows.some((row) => row.readiness_status === 'exception')
  const previewReady = summary
    ? (summary.gates?.preview_eligible === true)
    : rows.some((row) => row.readiness_status === 'preview_ready')

  const goNext = () => {
    window.dispatchEvent(
      new CustomEvent('decisionflow:navigate', {
        detail: { screen: SCREEN_ORDER[1] },
      })
    )
  }

  // Shared Tailwind class strings
  const thClass = 'px-3 py-2 text-left text-tok-xs font-semibold uppercase tracking-wide text-[rgb(var(--color-text-muted))] bg-[rgb(var(--color-surface-muted))] border-b border-[rgb(var(--color-border))]'
  const tdClass = 'px-3 py-2 text-tok-sm text-[rgb(var(--color-text))] border-b border-[rgb(var(--color-border))]'
  const btnPrimary = 'rounded-lg bg-[rgb(var(--color-primary))] px-4 py-2 text-tok-sm font-medium text-white hover:bg-[rgb(var(--color-primary-hover))] disabled:opacity-50 disabled:cursor-not-allowed'
  const btnSecondary = 'rounded-lg bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] px-4 py-2 text-tok-sm font-medium text-[rgb(var(--color-text))] hover:bg-[rgb(var(--color-surface-muted))] disabled:opacity-50 disabled:cursor-not-allowed'

  return (
    <section className="flex flex-col gap-6 p-6">
      {/* Header */}
      <header>
        <p className="text-tok-xs font-semibold uppercase tracking-widest text-[rgb(var(--color-text-muted))] mb-1">EXEC-01</p>
        <h2 className="text-tok-xl font-semibold text-[rgb(var(--color-text))]">Readiness Gate Dashboard</h2>
        <p className="mt-1 text-tok-sm text-[rgb(var(--color-text-muted))]">
          Review readiness status by entity before initiating preview or final calculation runs.
        </p>
      </header>

      {/* Toolbar */}
      <div className="flex items-center gap-3">
        <button type="button" className={btnSecondary} onClick={loadReadiness} disabled={loading}>
          {loading ? 'Refreshing…' : 'Refresh readiness'}
        </button>
        {lastLoadedAt && (
          <span className="text-tok-xs text-[rgb(var(--color-text-muted))]">Last loaded {lastLoadedAt.toLocaleTimeString()}</span>
        )}
      </div>

      {/* Error banner */}
      {errorMessage && (
        <div className="rounded-lg bg-[rgb(var(--color-danger))]/10 border border-[rgb(var(--color-danger))]/30 px-4 py-3 text-tok-sm text-[rgb(var(--color-danger))]">
          {errorMessage}
        </div>
      )}

      {/* Loading state */}
      {!errorMessage && loading && (
        <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">Loading readiness status…</p>
      )}

      {/* Empty state */}
      {!loading && !errorMessage && rows.length === 0 && (
        <div className="rounded-xl bg-[rgb(var(--color-surface-muted))] border border-[rgb(var(--color-border))] px-6 py-8 text-center text-tok-sm text-[rgb(var(--color-text-muted))]">
          No readiness records were returned for this fiscal period.
        </div>
      )}

      {/* Summary + entity grid */}
      {!loading && rows.length > 0 && (
        <>
          {/* Status summary */}
          <div className="grid grid-cols-3 gap-4">
            {[
              { label: 'Total records', value: summary?.entity_count ?? rows.length },
              { label: 'Overall status', value: summary?.data_readiness_status ?? '—' },
              { label: 'Preview eligible', value: previewReady ? 'Yes' : 'No' },
              { label: 'Blocking issues', value: summary?.blocking_issue_count ?? (hasBlockingException ? 'Yes' : 'No') },
            ].map(({ label, value }) => (
              <div key={label} className="rounded-xl bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] p-4 shadow-sm flex flex-col gap-1">
                <span className="text-tok-xs font-semibold uppercase tracking-wide text-[rgb(var(--color-text-muted))]">{label}</span>
                <strong className={[
                  'text-tok-lg font-semibold',
                  label === 'Blocking exception' && value === 'Yes' ? 'text-[rgb(var(--color-danger))]' : 'text-[rgb(var(--color-text))]'
                ].join(' ')}>{value}</strong>
              </div>
            ))}
          </div>

          {/* Entity cards */}
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {groupedRows.map(({ entityType, items }) => (
              <article key={entityType} className="rounded-xl bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] p-5 shadow-sm flex flex-col gap-3">
                <h3 className="text-tok-sm font-semibold text-[rgb(var(--color-text))] uppercase tracking-wide">{entityType}</h3>
                {items.length === 0 ? (
                  <p className="text-tok-sm text-[rgb(var(--color-text-muted))] italic">No readiness records.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full text-tok-sm border-collapse">
                      <thead>
                        <tr>
                          <th className={thClass}>Status</th>
                          <th className={thClass}>Entity ID</th>
                          <th className={thClass}>Updated</th>
                        </tr>
                      </thead>
                      <tbody>
                        {items.map((item, index) => {
                          const status = READINESS_STATUSES.includes(item.readiness_status)
                            ? item.readiness_status
                            : 'exception'

                          return (
                            // PATCH-S92-01: key and display use entity_uuid per DDL column name
                            <tr key={`${entityType}-${item.entity_uuid || index}`} className="hover:bg-[rgb(var(--color-surface-muted))]">
                              <td className={tdClass}>
                                <Pill tone={READINESS_PILL[status]?.tone ?? 'Draft'}>
                                  {READINESS_PILL[status]?.label ?? status}
                                </Pill>
                              </td>
                              <td className={tdClass}>{item.entity_uuid || '—'}</td>
                              <td className={tdClass}>{item.updated_at || '—'}</td>
                            </tr>
                          )
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </article>
            ))}
          </div>
        </>
      )}

      {/* Footer nav */}
      <footer className="flex flex-col gap-2 pt-4 border-t border-[rgb(var(--color-border))]">
        <div className="flex items-center gap-3">
          <button
            type="button"
            className={btnPrimary}
            onClick={goNext}
            disabled={loading || hasBlockingException}
          >
            Continue to calculation setup
          </button>
        </div>
        {hasBlockingException && (
          <p className="text-tok-sm text-[rgb(var(--color-danger))]">Resolve exception readiness records before continuing.</p>
        )}
      </footer>
    </section>
  )
}
